import { Scene, PBRMaterial, HemisphericLight, Mesh, CubeTexture, Vector3, MeshBuilder, Texture, HDRCubeTexture, Color3, ColorGradingTexture, DirectionalLight, StandardMaterial } from '@babylonjs/core';
import { GlobalObject } from './../utils/GlobalObject';

export class Skybox {
    private scene: Scene;
    private hdrSkyboxMaterial: PBRMaterial;
    private indoorLight?: DirectionalLight;
    private indoorLight2?: DirectionalLight;
    private sphericLight?: HemisphericLight;
    private outEnvTexture?: CubeTexture;
    private inEnvTexture?: CubeTexture | HDRCubeTexture;
    private outSkyTexture?: CubeTexture;
    private inSkyTexture?: CubeTexture;
    private outDoored: boolean = false;
    private source?: any;
    private fogColor = {
        night: new Color3(78 / 255, 65 / 255, 62 / 255),
        noon: new Color3(174 / 255, 175 / 255, 197 / 255),
    }
    //noon: new Color3(194 / 255, 233 / 255, 255 / 255),

    private hdrSkybox: Mesh;
    constructor(scene: Scene) {
        this.scene = scene;

        // 环境光
        this.sphericLight = new HemisphericLight("hemiLight", new Vector3(-1, 1, 0), this.scene);
        this.sphericLight.diffuse = new Color3(0.5, 0.4, 0.4);
        this.sphericLight.specular = new Color3(0.3, 0.2, 0.2);
        this.sphericLight.groundColor = new Color3(0.6, 0.6, 0.6);
        this.sphericLight.intensity = 1.0;
        // fog
        this.scene.fogMode = Scene.FOGMODE_LINEAR; //FOGMODE_NONE,FOGMODE_EXP,FOGMODE_EXP2,FOGMODE_LINEAR
        this.scene.fogStart = 600;
        this.scene.fogEnd = 5000;
        this.scene.fogDensity = 0.1;

        // 
        this.hdrSkyboxMaterial = new PBRMaterial("skyBox", this.scene);

        this.hdrSkybox = MeshBuilder.CreateBox("hdrSkyBox", { size: 11000.0 }, this.scene);
        this.hdrSkybox.isPickable = false;
        this.hdrSkybox.infiniteDistance = true;
        this.hdrSkybox.ignoreCameraMaxZ = true;
        this.source = GlobalObject.getInstance().main.source.skybox;
        GlobalObject.getInstance().setSkybox(this);

        this.createEnv();
        this.setOutdoorSkybox();
    }
    private createEnv() {
        this.indoorLight = new DirectionalLight("indoor_sun1", new Vector3(0, -1, 0), this.scene as any);
        this.indoorLight.position = new Vector3(0, 12, 0);
        this.indoorLight.diffuse = new Color3(1, 1, 0.98); // Orange sun color
        this.indoorLight.specular = new Color3(0.1, 0.1, 0.1);
        this.indoorLight.intensity = 6;
        this.indoorLight.setEnabled(false);

        this.indoorLight2 = new DirectionalLight("indoor_sun2", new Vector3(0, 1, 0), this.scene as any);
        this.indoorLight2.diffuse = new Color3(1, 1, 0.98); // Orange sun color
        this.indoorLight.specular = new Color3(0.1, 0.1, 0.1);
        this.indoorLight2.intensity = 2.8;
        this.indoorLight2.setEnabled(false);

        this.outEnvTexture = CubeTexture.CreateFromPrefilteredData(this.source.outEnv, this.scene as any);
        this.inEnvTexture = CubeTexture.CreateFromPrefilteredData(this.source.forest, this.scene as any);
        //this.inEnvTexture = new HDRCubeTexture(this.source.forest,this.scene,4096);

        this.outSkyTexture = new CubeTexture(this.source.outSky2, this.scene as any);
        this.inSkyTexture = CubeTexture.CreateFromPrefilteredData(this.source.inSky, this.scene as any);
        // 开启场景HDR
        (this.scene as any).hdrHighDynamicRange = true;
    }
    public setOutdoorSkybox() {
        if (this.outDoored == true) {
            return;
        }
        this.outDoored = true;
        this.indoorLight?.setEnabled(false);
        this.indoorLight2?.setEnabled(false);
        const sunLight = (GlobalObject.getInstance().main.sunLight as DirectionalLight);
        if (sunLight) {
            sunLight.setEnabled(true);
        }

        this.scene.fogColor = this.fogColor.noon;
        // scene 环境贴图
        (this.scene as any).environmentTexture = this.outEnvTexture;
        this.scene.environmentIntensity = 1.8; // 调高环境光照强度

        //hdr skybox material
        this.hdrSkyboxMaterial.backFaceCulling = false;
        this.hdrSkyboxMaterial.reflectionTexture = this.outSkyTexture as CubeTexture;
        this.hdrSkyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;
        this.hdrSkyboxMaterial.microSurface = 1.0 - 0;  // 0:模糊 ~1：清晰
        this.hdrSkyboxMaterial.disableLighting = true;
        this.hdrSkyboxMaterial.twoSidedLighting = true;
        this.hdrSkyboxMaterial.fogEnabled = false;
        this.hdrSkybox.material = this.hdrSkyboxMaterial;
        var colorGrading = new ColorGradingTexture(this.source.Grading, this.scene);
        this.hdrSkyboxMaterial.cameraColorGradingTexture = colorGrading;
        this.hdrSkyboxMaterial.cameraColorGradingEnabled = true;

        // 这里调整太阳光的强度
        colorGrading.level = 0.03;
        //环境光
        (this.sphericLight as HemisphericLight).diffuse = new Color3(0.5, 0.4, 0.4);
        (this.sphericLight as HemisphericLight).intensity = 0.3;
    }
    public setIndoorSkybox(intensity: number = 3) {
        if (this.outDoored == false) {
            return;
        }
        this.outDoored = false;
        this.indoorLight?.setEnabled(true);
        // this.indoorLight2?.setEnabled(true);
        (this.indoorLight as DirectionalLight).intensity = intensity;
        // (this.indoorLight2 as DirectionalLight).intensity = intensity;
        const sunLight = (GlobalObject.getInstance().main.sunLight as DirectionalLight);
        sunLight.setEnabled(false);
        this.scene.fogColor = this.fogColor.noon;
        // scene 环境贴图
        (this.scene as any).environmentTexture = this.inEnvTexture;
        this.scene.environmentIntensity = 2.0; // 环境光照强度
        console.log(this.scene.environmentIntensity);

        //hdr skybox material
        this.hdrSkyboxMaterial.backFaceCulling = false;
        this.hdrSkyboxMaterial.reflectionTexture = this.inSkyTexture as CubeTexture;
        this.hdrSkyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;
        this.hdrSkyboxMaterial.microSurface = 1.0 - 0.3;  // 0:模糊 ~1：清晰
        this.hdrSkyboxMaterial.disableLighting = true;
        this.hdrSkyboxMaterial.twoSidedLighting = true;
        this.hdrSkyboxMaterial.fogEnabled = false;
        this.hdrSkybox.material = this.hdrSkyboxMaterial;
        var colorGrading = new ColorGradingTexture(this.source.Grading, this.scene);
        this.hdrSkyboxMaterial.cameraColorGradingTexture = colorGrading;
        this.hdrSkyboxMaterial.cameraColorGradingEnabled = true;

        // 这里调整太阳光的强度
        colorGrading.level = 0.03;

        //环境光
        (this.sphericLight as HemisphericLight).diffuse = new Color3(1, 1, 1);
        (this.sphericLight as HemisphericLight).groundColor = new Color3(0.75, 0.75, 0.7);
        (this.sphericLight as HemisphericLight).intensity = 1;
    }
}
